// GoodweMonitorPanel — Goodwe (GW50K-MT) parkide võrdlusmonitooring stringirikete otsimiseks.
// Backend: /api/monitor/goodwe* (services/GoodweMonitorService.js, lib/goodweReport.js).
// AINULT lugemine: inverterit loetakse monitooringu ajal 10 s tagant ka siis, kui pargi tavapollimine
// on Markedroidi tõttu välja lülitatud.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DURATIONS = [1, 6, 12, 24];
const STATUS = {
  running: ['Käib', '#2563eb'], finalizing: ['Arvutan', '#7c3aed'], done: ['Valmis', '#16a34a'],
  stopped: ['Lõpetatud varem', '#d97706'], failed: ['Ebaõnnestus', '#dc2626'],
};
const SEV = { critical: ['Kriitiline', '#dc2626'], warning: ['Hoiatus', '#d97706'], info: ['Info', '#2563eb'] };
const STR_COLORS = ['#2563eb', '#60a5fa', '#16a34a', '#86efac', '#d97706', '#fcd34d', '#be185d', '#f9a8d4',
  '#7c3aed', '#c4b5fd', '#0f766e', '#5eead4', '#b91c1c', '#fca5a5', '#4b5563', '#9ca3af'];
const PARK_COLORS = ['#18181b', '#dc2626', '#2563eb', '#16a34a'];

const fmt = (v, d = 1) => (v == null || !Number.isFinite(Number(v)) ? '—'
  : Number(v).toLocaleString('et-EE', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtDT = (v) => (v ? new Date(v).toLocaleString('et-EE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtT = (ms) => new Date(ms).toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '14px 16px', marginBottom: 16 };
const label = { fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
const chip = (active) => ({
  padding: '4px 10px', borderRadius: 3, border: `1px solid ${active ? '#18181b' : 'var(--border)'}`,
  background: active ? '#18181b' : 'white', color: active ? 'white' : 'var(--text-2)',
  fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer',
});
const btn = (primary, color) => ({
  padding: '6px 12px', borderRadius: 4, border: `1px solid ${primary ? (color || '#166534') : 'var(--border-strong)'}`,
  background: primary ? (color || '#166534') : 'white', color: primary ? 'white' : 'var(--text-2)',
  fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
});
const th = { padding: '4px 6px', fontWeight: 600, textAlign: 'left' };
const td = { padding: '5px 6px', verticalAlign: 'top' };
const axisTick = { fontSize: 10, fill: '#71717a' };

export function GoodweMonitorPanel() {
  const [parks, setParks] = useState([]);
  const [intervalS, setIntervalS] = useState(10);
  const [selected, setSelected] = useState([]);
  const [hours, setHours] = useState(12);
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([fetch('/api/monitor/goodwe/parks'), fetch('/api/monitor/goodwe')]);
      const pj = await p.json(), sj = await s.json();
      if (!p.ok) throw new Error(pj.error || `HTTP ${p.status}`);
      setParks(pj.parks || []); setIntervalS(pj.interval_s || 10);
      setSessions(sj.sessions || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const running = sessions.filter((s) => s.status === 'running' || s.status === 'finalizing');
  useEffect(() => {
    if (!running.length) return undefined;
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [running.length, load]);
  useEffect(() => {
    if (selectedId == null && sessions.length) setSelectedId(sessions[0].id);
  }, [sessions, selectedId]);

  const loadDetail = useCallback(async (id) => {
    if (id == null) return;
    setLoading(true);
    try {
      const r = await fetch(`/api/monitor/goodwe/${id}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setDetail(j);
    } catch (e) { setError(e.message); setDetail(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (selectedId != null) loadDetail(selectedId); }, [selectedId, loadDetail]);
  const selStatus = sessions.find((s) => s.id === selectedId)?.status;
  useEffect(() => { if (selStatus === 'done' || selStatus === 'stopped') loadDetail(selectedId); }, [selStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const busySlots = new Set(running.flatMap((s) => s.slots));
  const toggle = (slot) => setSelected((cur) => (cur.includes(slot) ? cur.filter((x) => x !== slot) : [...cur, slot]));

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch('/api/monitor/goodwe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slots: selected, hours }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setSelected([]); setSelectedId(j.id); await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const stop = async (id) => {
    if (!window.confirm('Lõpeta monitooring nüüd? Aruanne koostatakse seni kogutud andmetest.')) return;
    await fetch(`/api/monitor/goodwe/${id}/stop`, { method: 'POST' });
    await load(); loadDetail(id);
  };
  const remove = async (id) => {
    if (!window.confirm('Kustuta see monitooring ja aruanne?')) return;
    const r = await fetch(`/api/monitor/goodwe/${id}`, { method: 'DELETE' });
    if (!r.ok) { setError((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`); return; }
    if (selectedId === id) { setSelectedId(null); setDetail(null); }
    load();
  };
  const download = (id, kind) => { window.location.href = `/api/monitor/goodwe/${id}/${kind}`; };

  return (
    <div>
      {error && (
        <div style={{ ...card, borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b', fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between' }}>
          <span>{error}</span><button onClick={() => setError(null)} style={{ ...btn(false), padding: '2px 8px' }}>×</button>
        </div>
      )}
      <div style={card}>
        {parks.length === 0 ? (
          <div style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>Selles vaates pole Goodwe inverteriga parke.</div>
        ) : (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 380px' }}>
              <div style={label}>Pargid võrdluseks · vali 1 või mitu</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {parks.map((p) => {
                  const inUse = busySlots.has(p.slot);
                  return (
                    <button key={p.slot} disabled={inUse} onClick={() => toggle(p.slot)} title={inUse ? 'Monitooring juba käib' : p.devices.map((d) => `${d.type} ${d.key}`).join(', ')}
                      style={{ ...chip(selected.includes(p.slot)), opacity: inUse ? 0.45 : 1 }}>
                      #{p.slot} {p.name}{inUse ? ' · käib' : ''}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={label}>Kestus</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {DURATIONS.map((h) => <button key={h} onClick={() => setHours(h)} style={chip(hours === h)}>{h} h</button>)}
              </div>
            </div>
            <button onClick={start} disabled={busy || !selected.length} style={{ ...btn(true), opacity: busy || !selected.length ? 0.5 : 1 }}>
              ▶ Alusta {selected.length > 1 ? `võrdlust (${selected.length} parki)` : 'monitooringut'}
            </button>
            <div style={{ flex: '1 1 100%', fontSize: '0.66rem', color: 'var(--text-3)' }}>
              Inverterit loetakse {intervalS} s tagant ainult lugemisrežiimis (FC03): 4 MPPT pinge/vool, 8 stringi voolu, AC faasid, temperatuur,
              isolatsioonitakistus, lekkevool, vea- ja hoiatuskoodid. Lugemine toimub ka siis, kui pargi tavapollimine on Markedroidi tõttu välja lülitatud.
              Pimedas on inverter väljas — alusta hommikul või lase käia üle öö.
            </div>
          </div>
        )}
      </div>

      {sessions.length > 0 && (
        <div style={card}>
          <div style={label}>Monitooringud</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
            <thead><tr style={{ color: 'var(--text-3)', fontSize: '0.6rem', textTransform: 'uppercase' }}>
              <th style={th}>Algus</th><th style={th}>Kestus</th><th style={th}>Pargid</th><th style={th}>Olek</th><th style={th}>Leiud</th><th style={th}>Alustas</th><th style={th} />
            </tr></thead>
            <tbody>
              {sessions.map((s) => {
                const [st, col] = STATUS[s.status] || [s.status, '#71717a'];
                return (
                  <tr key={s.id} onClick={() => setSelectedId(s.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', background: s.id === selectedId ? '#f4f4f5' : 'transparent' }}>
                    <td style={td}>{fmtDT(s.started_at)}</td>
                    <td style={td}>{Math.round((s.ends_at - s.started_at) / 3600_000)} h</td>
                    <td style={td}>{s.parks.map((p) => p.name).join(' + ')}</td>
                    <td style={td}><span style={{ color: col, fontWeight: 600 }}>{st}</span></td>
                    <td style={td}>{s.summary ? (s.summary.critical ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{s.summary.critical} kriitilist · {s.summary.findings} kokku</span> : `${s.summary.findings}`) : (s.error || '—')}</td>
                    <td style={{ ...td, color: 'var(--text-3)' }}>{s.created_by || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => download(s.id, 'csv')} style={{ ...btn(false), padding: '3px 8px', marginRight: 4 }}>CSV</button>
                      <button onClick={() => download(s.id, 'export')} style={{ ...btn(false), padding: '3px 8px', marginRight: 4 }}>JSON</button>
                      {s.status === 'running' && <button onClick={() => stop(s.id)} style={{ ...btn(true, '#dc2626'), padding: '3px 8px' }}>■</button>}
                      {s.status !== 'running' && s.status !== 'finalizing' && <button onClick={() => remove(s.id)} style={{ ...btn(false), padding: '3px 8px', color: '#dc2626' }}>×</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && <div style={{ ...card, fontSize: '0.75rem', color: 'var(--text-3)' }}>Laen aruannet…</div>}
      {!loading && detail?.report && <GoodweReportView detail={detail} onRefresh={() => loadDetail(detail.id)} />}
      {!loading && detail && !detail.report && <div style={{ ...card, fontSize: '0.75rem', color: 'var(--text-3)' }}>Aruannet pole — {detail.error || 'andmed puuduvad'}.</div>}
    </div>
  );
}

function GoodweReportView({ detail, onRefresh }) {
  const rep = detail.report;
  const parks = rep.parks || [];
  const slots = parks.map((p) => p.slot);
  // Parkide võimsus ja MPPT pinged ühes ajateljes
  const merged = useMemo(() => {
    const by = new Map();
    parks.forEach((p) => p.series_1min.forEach((b) => {
      if (!by.has(b.ts)) by.set(b.ts, { ts: b.ts });
      const o = by.get(b.ts);
      o[`pac_${p.slot}`] = b.pac;
      for (let n = 1; n <= 6; n++) if (b[`pv${n}_v`] != null) o[`v${n}_${p.slot}`] = b[`pv${n}_v`];
    }));
    return [...by.values()].sort((a, b) => a.ts - b.ts);
  }, [parks]);
  const xAxis = <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={fmtT} tick={axisTick} minTickGap={40} />;
  const tooltip = <Tooltip labelFormatter={(v) => fmtDT(v)} formatter={(v, n) => [fmt(v, 2), n]} contentStyle={{ fontSize: 11 }} />;
  const findings = rep.findings || [];

  return (
    <div>
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={label}>{rep.partial ? 'Vahearuanne (monitooring käib)' : 'Võrdlusaruanne'}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{parks.map((p) => p.name).join(' vs ')} · {fmtDT(rep.session?.started_at)} – {fmtDT(rep.period?.to)}</div>
        </div>
        {rep.partial && <button onClick={onRefresh} style={btn(false)}>Värskenda</button>}
      </div>

      <div style={card}>
        <div style={label}>Leiud</div>
        {findings.length === 0 && <div style={{ fontSize: '0.75rem', color: '#16a34a' }}>Kõrvalekaldeid ei leitud.</div>}
        {findings.map((x, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
            <span style={{ flex: 'none', width: 70, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: SEV[x.severity]?.[1] }}>
              {SEV[x.severity]?.[0]}<div style={{ color: 'var(--text-3)', fontWeight: 500 }}>{x.park}</div>
            </span>
            <div><div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{x.title}</div><div style={{ fontSize: '0.68rem', color: 'var(--text-2)' }}>{x.detail}</div></div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(parks.length, 4)}, minmax(0, 1fr))`, gap: 16 }}>
        {parks.map((p) => (
          <div key={p.slot} style={card}>
            <div style={label}>{p.name}{p.info?.serial ? ` · SN ${p.info.serial}` : ''}</div>
            <table style={{ width: '100%', fontSize: '0.7rem', borderCollapse: 'collapse' }}><tbody>
              {[
                ['Toodang AC (Pac integraal)', `${fmt(p.energy_kwh, 1)} kWh`], ['DC (MPPT-de summa)', p.dc_energy_kwh != null ? `${fmt(p.dc_energy_kwh, 1)} kWh` : '—'],
                ['Loenduri järgi', p.energy_counter_kwh != null ? `${fmt(p.energy_counter_kwh, 1)} kWh` : '—'],
                ['Tippvõimsus', `${fmt(p.peak_kw, 1)} kW`], ['Mõõtmisi (online)', `${p.coverage.online_points} / ${p.coverage.expected}`],
                ['Isolatsioon min / mediaan', `${fmt(p.iso_kohm.min, 0)} / ${fmt(p.iso_kohm.median, 0)} kΩ`],
                ['Lekkevool / GFCI max', `${fmt(p.leak_ma_max, 0)} / ${fmt(p.gfci_ma_max, 0)} mA`],
                ['Temperatuur max', `${fmt(p.temp_max_c, 1)} °C`],
                ['Tööolek', Object.entries(p.work_mode_minutes || {}).map(([k, v]) => `${k} ${v} min`).join(', ') || '—'],
                ['Veakoodid', p.errors.length ? p.errors.join(', ') : 'pole'],
                ['Stringid → MPPT', p.mapping.map((m) => `M${m.mppt}: ${m.strings.join('+') || '—'}${m.assumed ? '?' : ''}`).join(' · ')
                  + (p.unmapped_strings?.length ? ` · eraldi: ${p.unmapped_strings.join(', ')}` : '')],
                ['Võimsuspiirang aktiivne', p.power_limit_minutes != null ? `${fmt(p.power_limit_minutes, 0)} min` : '—'],
                ['Püsivara', p.info ? `${p.info.firmware ?? '—'} / ARM ${p.info.arm_version ?? '—'}` : '—'],
              ].map(([k, v]) => <tr key={k} style={{ borderTop: '1px solid var(--border)' }}><td style={{ ...td, color: 'var(--text-3)' }}>{k}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{v}</td></tr>)}
            </tbody></table>
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={label}>Stringid — laeng (Ah), max vool ja võrdlus</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
          <thead><tr style={{ color: 'var(--text-3)', fontSize: '0.6rem', textTransform: 'uppercase' }}>
            <th style={th}>String</th>
            {parks.map((p) => <th key={p.slot} style={th} colSpan={3}>{p.name} · Ah / max A / sama MPPT suhe</th>)}
            {parks.length > 1 && parks.map((p) => <th key={`v${p.slot}`} style={th}>{p.name} vs teised</th>)}
          </tr></thead>
          <tbody>
            {rep.comparison.strings.map((row) => (
              <tr key={row.string} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ ...td, fontWeight: 600 }}>{row.string} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>{parks.map((p) => (row.by_park[p.slot]?.mppt ? `M${row.by_park[p.slot].mppt}` : '–')).join('/')}</span></td>
                {parks.map((p) => {
                  const c = row.by_park[p.slot] || {};
                  const bad = c.sibling_ratio != null && c.sibling_ratio < 0.85;
                  return [
                    <td key="a" style={td}>{fmt(c.ah, 1)}</td>,
                    <td key="m" style={td}>{fmt(c.max_a, 1)}</td>,
                    <td key="r" style={{ ...td, color: bad ? '#dc2626' : 'var(--text-2)', fontWeight: bad ? 700 : 400 }}>{c.sibling_ratio != null ? fmt(c.sibling_ratio, 2) : '—'}</td>,
                  ];
                })}
                {parks.length > 1 && parks.map((p) => {
                  const v = row.by_park[p.slot]?.vs_others;
                  const bad = v != null && v < 0.85;
                  return <td key={`v${p.slot}`} style={{ ...td, color: bad ? '#dc2626' : 'var(--text-2)', fontWeight: bad ? 700 : 400 }}>{v != null ? `${fmt(v * 100, 0)} %` : '—'}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', marginTop: 6 }}>
          „Sama MPPT suhe" = stringi vool / sama MPPT teiste stringide keskmine kõrge kiirguse ajal (mediaan); paralleelstringidel peaks olema ≈ 1,00. Alla 0,85 = nõrk string.
          „vs teised" võrdleb sama numbriga stringi teistes parkides — kehtib ainult siis, kui parkide paigutus on sama.
        </div>
      </div>

      <div style={card}>
        <div style={label}>MPPT — toodang, tööpinge (kõrge kiirguse ajal) ja koidiku Voc</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
          <thead><tr style={{ color: 'var(--text-3)', fontSize: '0.6rem', textTransform: 'uppercase' }}>
            <th style={th}>MPPT</th>{parks.map((p) => <th key={p.slot} style={th} colSpan={3}>{p.name} · kWh / Vmp / Voc</th>)}
          </tr></thead>
          <tbody>
            {rep.comparison.mppt.map((row) => (
              <tr key={row.mppt} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ ...td, fontWeight: 600 }}>MPPT{row.mppt}</td>
                {parks.map((p) => {
                  const c = row.by_park[p.slot] || {};
                  return [<td key="k" style={td}>{fmt(c.kwh, 1)}{c.unmeasured_a ? <span style={{ color: '#d97706' }}> (+{fmt(c.unmeasured_a, 1)} A mõõtmata)</span> : ''}</td>,
                          <td key="v" style={td}>{fmt(c.vmp_v, 0)} V</td>, <td key="o" style={td}>{fmt(c.voc_dawn_v, 0)} V</td>];
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {parks.length > 0 && (
        <div style={card}>
          <div style={label}>Võimsus (kW) — parkide võrdlus</div>
          <div style={{ height: 220 }}><ResponsiveContainer width="100%" height="100%">
            <LineChart data={merged} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />{xAxis}<YAxis tick={axisTick} />{tooltip}<Legend wrapperStyle={{ fontSize: 11 }} />
              {parks.map((p, i) => <Line key={p.slot} dataKey={`pac_${p.slot}`} name={p.name} stroke={PARK_COLORS[i % 4]} dot={false} isAnimationActive={false} />)}
            </LineChart>
          </ResponsiveContainer></div>
        </div>
      )}

      {rep.comparison.mppt.map((row) => row.mppt).map((n) => (
        <div key={n} style={card}>
          <div style={label}>MPPT{n} pinge (V) — parkide võrdlus</div>
          <div style={{ height: 160 }}><ResponsiveContainer width="100%" height="100%">
            <LineChart data={merged} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />{xAxis}<YAxis tick={axisTick} domain={['auto', 'auto']} />{tooltip}<Legend wrapperStyle={{ fontSize: 11 }} />
              {parks.map((p, i) => <Line key={p.slot} dataKey={`v${n}_${p.slot}`} name={p.name} stroke={PARK_COLORS[i % 4]} dot={false} isAnimationActive={false} />)}
            </LineChart>
          </ResponsiveContainer></div>
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(parks.length, 2)}, minmax(0, 1fr))`, gap: 16 }}>
        {parks.map((p) => (
          <div key={p.slot} style={card}>
            <div style={label}>{p.name} — stringivoolud (A)</div>
            <div style={{ height: 240 }}><ResponsiveContainer width="100%" height="100%">
              <LineChart data={p.series_1min} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />{xAxis}<YAxis tick={axisTick} />{tooltip}<Legend wrapperStyle={{ fontSize: 11 }} />
                {p.strings.map((x) => x.string).map((k) => <Line key={k} dataKey={`str${k}_i`} name={`S${k}`} stroke={STR_COLORS[(k - 1) % 16]} dot={false} strokeWidth={1.3} isAnimationActive={false} />)}
              </LineChart>
            </ResponsiveContainer></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GoodweMonitorPanel;
